import { useEffect, useState } from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import type { BuildVerdict } from '@internal/backstage-plugin-release-common';

export interface BuildStatus {
  sha: string | null;
  verdict: BuildVerdict;
}

/**
 * Whether each component's latest commit has an image behind it.
 *
 * A courtesy, not a control — the scaffolder action asks the same question
 * before it tags. The point of asking here is timing: without it the operator
 * only learns after choosing, confirming, and watching a task refuse.
 *
 * A failure resolves to an empty map rather than an error state. Not knowing
 * is the normal case for a repository the portal cannot read, and it must not
 * block a release the action would have allowed.
 */
export function useBuildStatus(
  services: string[],
  generation = 0,
): Record<string, BuildStatus> {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [byService, setByService] = useState<Record<string, BuildStatus>>({});

  // The caller builds a fresh array every render; effect deps must be stable.
  const key = services.join(',');

  useEffect(() => {
    if (key === '') {
      setByService({});
      return undefined;
    }

    let cancelled = false;

    const load = async () => {
      const baseUrl = await discoveryApi.getBaseUrl('release');
      const entries = await Promise.all(
        key.split(',').map(async service => {
          const response = await fetchApi.fetch(
            `${baseUrl}/build-status?service=${encodeURIComponent(service)}${
              generation > 0 ? '&refresh=true' : ''
            }`,
          );
          if (!response.ok) {
            return null;
          }
          const body = (await response.json()) as BuildStatus;
          return [service, body] as const;
        }),
      );
      if (!cancelled) {
        setByService(
          Object.fromEntries(entries.filter(Boolean) as [string, BuildStatus][]),
        );
      }
    };

    load().catch(() => {
      if (!cancelled) {
        setByService({});
      }
    });
    return () => {
      cancelled = true;
    };
  }, [discoveryApi, fetchApi, key, generation]);

  return byService;
}
